import { useState, type ReactNode } from "react";

/**
 * Behavioural-only candidate avatar wrapper.
 *
 * Renders the avatar <img> when a usable src is present AND it loads.
 * Falls back to the supplied `fallback` node when:
 *   - src is missing/empty, OR
 *   - the image fails to load (e.g. S3 403/404, expired licdn URL).
 *
 * The fallback JSX is owned by each call site (its existing initials
 * block, passed verbatim) so this component never changes any site's
 * visual styling — it only guarantees the load-failure path no longer
 * shows a broken-image icon.
 */
export function CandidateAvatarImage({
  src,
  alt,
  className,
  fallback,
}: {
  src?: string | null;
  alt: string;
  className?: string;
  fallback: ReactNode;
}) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) return <>{fallback}</>;

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setFailed(true)}
    />
  );
}
